import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "already_verified" | "expired" | "invalid" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");
      
      if (!token) {
        setStatus("invalid");
        setMessage("Token de verificação não encontrado.");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("verify-email", {
          body: { token },
        });

        if (error) {
          throw error;
        }

        if (data.success) {
          if (data.alreadyVerified) {
            setStatus("already_verified");
            setMessage(data.message);
          } else {
            setStatus("success");
            setMessage(data.message);
          }
        } else {
          if (data.error === "token_expired") {
            setStatus("expired");
          } else if (data.error === "invalid_token") {
            setStatus("invalid");
          } else {
            setStatus("error");
          }
          setMessage(data.message);
        }
      } catch (error: any) {
        console.error("Verification error:", error);
        setStatus("error");
        setMessage("Erro ao verificar e-mail. Por favor, tente novamente.");
      }
    };

    verifyEmail();
  }, [searchParams]);

  const getIcon = () => {
    switch (status) {
      case "loading":
        return <Loader2 className="w-16 h-16 animate-spin text-primary" />;
      case "success":
      case "already_verified":
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case "expired":
        return <AlertTriangle className="w-16 h-16 text-amber-500" />;
      case "invalid":
      case "error":
        return <XCircle className="w-16 h-16 text-destructive" />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case "loading":
        return "Verificando...";
      case "success":
        return "E-mail Confirmado!";
      case "already_verified":
        return "E-mail Já Confirmado";
      case "expired":
        return "Link Expirado";
      case "invalid":
        return "Link Inválido";
      case "error":
        return "Erro na Verificação";
    }
  };

  return (
    <Layout>
      <SEO 
        title="Verificação de E-mail - TUPJA"
        description="Verificação de e-mail da plataforma TUPJA"
      />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 px-4 py-20">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="flex justify-center mb-4">
              {getIcon()}
            </div>
            <CardTitle className="text-2xl font-display text-primary">
              {getTitle()}
            </CardTitle>
            {message && (
              <CardDescription className="text-base mt-2">
                {message}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {status === "success" && (
              <>
                <p className="text-muted-foreground">
                  Sua conta foi ativada com sucesso! Agora você pode fazer login e acessar todos os serviços da TUPJA.
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => navigate("/auth")}
                >
                  Fazer Login
                </Button>
              </>
            )}

            {status === "already_verified" && (
              <>
                <p className="text-muted-foreground">
                  Seu e-mail já foi confirmado anteriormente. Você pode fazer login normalmente.
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => navigate("/auth")}
                >
                  Ir para Login
                </Button>
              </>
            )}

            {status === "expired" && (
              <>
                <p className="text-muted-foreground">
                  O link de verificação expirou. Por favor, faça login e solicite um novo e-mail de confirmação.
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => navigate("/auth")}
                >
                  Voltar para Login
                </Button>
              </>
            )}

            {(status === "invalid" || status === "error") && (
              <>
                <p className="text-muted-foreground">
                  Não foi possível verificar seu e-mail. O link pode já ter sido utilizado ou estar incorreto.
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => navigate("/auth")}
                >
                  Voltar para Login
                </Button>
              </>
            )}

            {status === "loading" && (
              <p className="text-muted-foreground">
                Aguarde enquanto verificamos seu e-mail...
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default VerifyEmail;
